/**
 * View Manager Component
 *
 * Purpose: Manages views with CRUD operations, field assignment, and ordering.
 *          Views define how fields are organized and displayed for specific models.
 *
 * Architecture: Table-based interface using MDTable component with modal forms
 *              for create/edit operations. Includes drag-and-drop field ordering.
 *
 * Dependencies: React, Material-UI, MDTable, MetadataService
 * Author: Development Team
 * Created: 2025-01-27
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, TextField, Chip, Autocomplete, ListSubheader } from '@mui/material';
import {
  Public as GlobalIcon,
  Business as TenantIcon,
  Restore as RestoreIcon,
} from '@mui/icons-material';
import MDTable from '../../../components/md-table';
import { getScopeChipColor } from '../../../constants/mdm';
import { TableColumn } from '../../../types/table';
import metadataService, {
  View,
  ViewFormData,
  Model,
  TenantInfo,
} from '../../../services/metadataService';
import { formatAuditTimestamp, formatAuditUser } from '../../../utils/formatting';
import { ViewModal } from '../../../components/metadata/modals/ViewModal';
import { useViewData, useViewDialogs } from '../../../hooks/useViewManager';
import {
  filterViewsByModel,
  prepareViewStatusToggleData,
  getToggledStatus,
} from '../../../utils/viewHelpers';
import { RowActionButton, RowActions, StatusChip } from '../../../components/common';
import { RevertOverrideDialog } from '../../../components/metadata/RevertOverrideDialog';

// Component interfaces
interface ViewManagerProps {
  tenantContext: TenantInfo;
  canEdit: boolean;
  userPermissions: {
    can_edit_global: boolean;
    can_edit_tenant: boolean;
    can_switch_context: boolean;
  };
}

/**
 * View Manager Component
 */
const ViewManager: React.FC<ViewManagerProps> = ({
  tenantContext,
  canEdit,
  userPermissions: _userPermissions,
}) => {
  // Filters
  const [filters, setFilters] = useState({
    model_id: '',
  });

  // Use custom hooks for data and dialog management
  const { data, models, loading, error, loadData, loadModels } = useViewData(
    tenantContext.tenant_id
  );

  const { modalOpen, modalMode, selectedView, openCreateModal, openEditModal, closeModal } =
    useViewDialogs();

  // Revert state
  const [revertDialogOpen, setRevertDialogOpen] = useState(false);
  const [revertingView, setRevertingView] = useState<View | null>(null);
  const [globalView, setGlobalView] = useState<View | null>(null);
  const [revertLoading, setRevertLoading] = useState(false);

  // Filter data based on model filter
  const filteredData = useMemo(() => {
    return filterViewsByModel(data, filters.model_id);
  }, [data, filters.model_id]);

  // Create custom toolbar filters
  const customToolbarFilters = useMemo(
    () => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {/* Model Filter */}
        <Autocomplete
          data-testid="model-filter-combobox"
          size="small"
          disablePortal
          options={[
            { model_id: '', name: 'All Models' } as unknown as Model,
            ...models.flatMap((group) => group.options),
          ]}
          groupBy={(option) => {
            // Do not group the "All Models" option
            if ((option as any).model_id === '') return '';
            const group = models.find((g) => g.options.includes(option));
            return group?.group || '';
          }}
          renderGroup={(params) =>
            params.group ? (
              <React.Fragment key={params.key}>
                <ListSubheader
                  sx={{ fontSize: '0.75rem', fontWeight: 'bold', color: 'text.secondary' }}
                >
                  {params.group}
                </ListSubheader>
                {params.children}
              </React.Fragment>
            ) : (
              <React.Fragment key={params.key}>{params.children}</React.Fragment>
            )
          }
          getOptionLabel={(option) =>
            (option as any).model_id === '' ? 'All Models' : option.name
          }
          isOptionEqualToValue={(option, value) => option.model_id === value.model_id}
          value={(() => {
            const allOption = { model_id: '', name: 'All Models' } as unknown as Model;
            const all = [allOption, ...models.flatMap((group) => group.options)];
            return all.find((m) => m.model_id === (filters.model_id || '')) || allOption;
          })()}
          onChange={(_event, newValue) => {
            setFilters((prev) => ({ ...prev, model_id: newValue?.model_id || '' }));
          }}
          renderInput={(params) => (
            <TextField {...params} label="Model" placeholder="Search models..." size="small" />
          )}
          sx={{ minWidth: 220 }}
          clearOnEscape
        />
      </Box>
    ),
    [filters.model_id, models]
  );

  // Load data on component mount
  useEffect(() => {
    loadModels();
    loadData();
  }, [loadModels]);

  // Reload data when filters change
  useEffect(() => {
    loadData();
  }, [filters.model_id]);

  // Table columns configuration
  const columns: TableColumn[] = [
    {
      field: 'label',
      headerName: 'Label',
      width: 200,
      sortable: true,
    },
    {
      field: 'view_id',
      headerName: 'View ID',
      width: 200,
      sortable: true,
      renderCell: (value: string) => (
        <Box sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>{value}</Box>
      ),
    },
    {
      field: 'tenant_id',
      headerName: 'Scope',
      width: 120,
      sortable: true,
      renderCell: (value: string, row: View) => {
        // Use scope_info from API response if available, otherwise fall back to tenant_id logic
        const scopeInfo = row.scope_info;
        let scopeLabel = '';
        let scopeIcon = <TenantIcon fontSize="small" />;

        if (scopeInfo) {
          // Use scope_info from backend
          scopeLabel = scopeInfo.scope;
          // Note: Backend might return 'Overridden' instead of 'OVERRIDDEN'
          if (scopeLabel === 'Overridden') {
            scopeLabel = 'OVERRIDDEN';
          }
          scopeIcon =
            scopeLabel === 'GLOBAL' ? (
              <GlobalIcon fontSize="small" />
            ) : (
              <TenantIcon fontSize="small" />
            );
        } else {
          // Fallback to tenant_id logic
          if (value === 'GLOBAL') {
            scopeLabel = 'GLOBAL';
            scopeIcon = <GlobalIcon fontSize="small" />;
          } else {
            scopeLabel = 'LOCAL';
            scopeIcon = <TenantIcon fontSize="small" />;
          }
        }

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {scopeIcon}
            <Chip
              label={scopeLabel}
              size="small"
              color={getScopeChipColor(scopeLabel)}
              variant="outlined"
            />
          </Box>
        );
      },
    },
    {
      field: 'model_id',
      headerName: 'Model',
      width: 150,
      sortable: true,
      renderCell: (value: string, row: View) => {
        const modelName = row.model_name || value;
        const modelType = row.model_type;
        const isFieldDef = modelType === 'fielddef';

        const handleModelClick = (event: React.MouseEvent) => {
          event.stopPropagation(); // Prevent event from bubbling up to table row
          setFilters((prev) => ({ ...prev, model_id: value }));
        };

        return (
          <Chip
            label={modelName}
            size="small"
            variant="outlined"
            color={isFieldDef ? 'secondary' : 'primary'}
            onClick={handleModelClick}
            sx={{
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: isFieldDef ? 'secondary.dark' : 'primary.dark',
                transform: 'scale(1.05)',
                transition: 'all 0.2s ease-in-out',
              },
            }}
          />
        );
      },
    },
    {
      field: 'fields',
      headerName: 'Fields',
      width: 100,
      sortable: false,
      renderCell: (_value: any, row: View) => {
        const fieldCount = row.field_count || 0;
        return (
          <Chip
            label={`${fieldCount} field${fieldCount !== 1 ? 's' : ''}`}
            size="small"
            variant="outlined"
          />
        );
      },
    },
    {
      field: 'updated_at',
      headerName: 'Modified Date',
      width: 140,
      sortable: true,
      renderCell: (value: string) => formatAuditTimestamp(value),
    },
    {
      field: 'updated_by',
      headerName: 'Modified By',
      width: 120,
      sortable: true,
      renderCell: (value: string, row: View) => formatAuditUser(value, row.updated_by_user),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 100,
      sortable: true,
      renderCell: (value: string) => <StatusChip status={value} />,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (_value: any, row: View) => (
        <RowActions
          entityLabel="View"
          rowLabel={row.label}
          testIdPrefix="view"
          onEdit={() => handleEdit(row)}
          secondaryActions={
            canRevertView(row) && (
              <RowActionButton
                label="Revert to Global Version"
                testId="view-revert"
                onClick={() => handleRevertView(row)}
                disabled={!canEdit}
                icon={<RestoreIcon fontSize="small" color={canEdit ? 'warning' : 'disabled'} />}
              />
            )
          }
          deactivation={{
            variant: 'disable',
            isActive: row.status !== 'disabled',
            onToggle: () => handleStatusToggle(row),
            disabled: !canEdit,
          }}
        />
      ),
    },
  ];

  // Event handlers
  const handleCreate = () => {
    openCreateModal();
  };

  const handleEdit = (view: View) => {
    openEditModal(view);
  };

  const handleRowClick = (row: View) => {
    handleEdit(row);
  };

  const handleSave = async (formData: ViewFormData) => {
    if (modalMode === 'create') {
      await metadataService.createView(formData, tenantContext.tenant_id);
    } else if (selectedView) {
      await metadataService.updateView(
        selectedView.view_id,
        formData,
        selectedView.tenant_id || 'GLOBAL',
        tenantContext.tenant_id
      );
    }
    loadData();
  };

  const handleDelete = async (view: View) => {
    await metadataService.deleteView(view.view_id);
    loadData();
  };

  const handleStatusToggle = async (view: View) => {
    const newStatus = getToggledStatus(view.status);
    const formData = prepareViewStatusToggleData(view, newStatus);
    await metadataService.updateView(
      view.view_id,
      formData,
      view.tenant_id || 'GLOBAL',
      tenantContext.tenant_id
    );
    loadData();
  };

  // Helper function to check if a view can be reverted
  const canRevertView = (view: View): boolean => {
    const scopeInfo = view.scope_info;
    if (!scopeInfo) {
      return false;
    }
    return scopeInfo.scope === 'OVERRIDDEN' || (scopeInfo.scope as string) === 'Overridden';
  };

  // Revert handlers
  const handleRevertView = useCallback(
    async (view: View) => {
      setRevertingView(view);

      // First try to find the GLOBAL view from the existing data
      let globalViewData = data.find((v) => v.view_id === view.view_id && v.tenant_id === 'GLOBAL');

      // If not found in current data, fetch GLOBAL views explicitly
      if (!globalViewData) {
        try {
          const globalViews = await metadataService.getViews('GLOBAL');
          globalViewData = globalViews.find((v) => v.view_id === view.view_id);
        } catch (error) {
          console.error('Failed to fetch GLOBAL views:', error);
        }
      }

      if (globalViewData) {
        setGlobalView(globalViewData);
      } else {
        console.warn('GLOBAL view not found for:', view.view_id);
        setGlobalView(null);
      }

      setRevertDialogOpen(true);
    },
    [data]
  );

  const handleRevertConfirm = useCallback(async () => {
    if (!revertingView) return;

    setRevertLoading(true);
    try {
      await metadataService.revertView(revertingView.view_id, revertingView.tenant_id);
      setRevertDialogOpen(false);
      setRevertingView(null);
      setGlobalView(null);
      // Wait for data to reload before closing loading state
      await loadData();
    } catch (error) {
      console.error('Failed to revert view:', error);
      // Error handling could be enhanced with a snackbar notification
    } finally {
      setRevertLoading(false);
    }
  }, [revertingView, loadData]);

  const handleRevertClose = useCallback(() => {
    setRevertDialogOpen(false);
    setRevertingView(null);
    setGlobalView(null);
    setRevertLoading(false);
  }, []);

  return (
    <Box>
      {/* Data Table */}
      <Box>
        <MDTable
          mode="manual"
          manual={{
            columns,
            title: 'Views',
            description: `${filteredData.length} view(s) found`,
          }}
          data={filteredData.map((item) => ({ ...item, id: item.view_id }))}
          loading={loading}
          error={error}
          onRowClick={handleRowClick}
          onRefresh={loadData}
          onAddNew={canEdit ? handleCreate : undefined}
          showRefreshButton={true}
          showAddButton={canEdit}
          selectable={false}
          defaultSortModel={[{ field: 'label', sort: 'asc' }]}
          sortable={true}
          filterable={true}
          customToolbarFilters={customToolbarFilters}
        />
      </Box>

      {/* Create/Edit Modal */}
      <ViewModal
        open={modalOpen}
        mode={modalMode}
        view={selectedView}
        tenantContext={tenantContext}
        canEdit={canEdit}
        onClose={closeModal}
        onSave={handleSave}
        onDelete={handleDelete}
      />

      {/* Revert Dialog */}
      <RevertOverrideDialog
        open={revertDialogOpen}
        onClose={handleRevertClose}
        onConfirm={handleRevertConfirm}
        overrideItem={revertingView}
        globalItem={globalView}
        loading={revertLoading}
        itemType="view"
      />
    </Box>
  );
};

export default ViewManager;
